import { FormState } from 'informed';
import { useMutation } from 'react-query';
import feathersClient from '../../apiClient';
import getResponseArray from '@tymbe/utils/getResponseArray';
import { AdminPopup } from '@tymbe/schema/admin-popup.interface';
import { ErrorAlert, SuccessAlert } from '../../components/alerts';
import {
  AdminPopupFormValues,
  adminPopupModuleOptions,
  createAdminPopupWeekdayOptions,
  mapLabelValueToValues,
  useCompaniesWithBranchoffices,
} from './adminPopupUtils';
import moment from 'moment';
import AdminPopupForm from './AdminPopupForm';

interface EditAdminPopupProps {
  initialValues: AdminPopup;
  onClose: () => void;
}

export default function EditAdminPopup({ initialValues, onClose }: EditAdminPopupProps) {
  const { data, isLoading } = useCompaniesWithBranchoffices();
  const companies = getResponseArray(data);
  const updateMutation = useMutation((data: Partial<AdminPopup>) =>
    feathersClient.service('admin-popup').patch(initialValues.id, data)
  );

  const mapCompanyIdsToLabelValue = (companyIds: number[] | null | undefined) => {
    return (
      companyIds?.map((id) => {
        const companyById = companies.find((company) => company.id === id);
        return {
          value: id,
          label: `[${id}]${companyById?.name ? ' ' + companyById?.name : ''}`,
        };
      }) || []
    );
  };

  const moduleValueToLabel = (value: string) => {
    const found = adminPopupModuleOptions.find((opt) => opt.value === value);
    return found ? found.label : value;
  };

  const frequencyWeekdayValueToLabel = (value: number) => {
    const found = createAdminPopupWeekdayOptions.find((opt) => opt.value === value);
    return found ? found.label : `${value}`;
  };

  const mapModuleValuesToLabelValue = (moduleValues: string[]) => {
    return (
      moduleValues?.map((v) => ({
        value: v,
        label: moduleValueToLabel(v),
      })) || []
    );
  };

  const mapWeekdayFrequencyToLabelValue = (weekdayValues: number[]) => {
    return (
      weekdayValues.map((v) => ({
        value: v,
        label: frequencyWeekdayValueToLabel(v),
      })) || []
    );
  };

  // Convert initialValues to form values
  const formInitialValues: AdminPopupFormValues = {
    ...initialValues,
    from: initialValues.from ? moment(initialValues.from) : null,
    to: initialValues.to ? moment(initialValues.to) : null,
    company_ids: mapCompanyIdsToLabelValue(initialValues.company_ids),
    show_on_modul_page: mapModuleValuesToLabelValue(initialValues.show_on_modul_page),
    display_frequency_weekday: initialValues.display_frequency_weekday
      ? mapWeekdayFrequencyToLabelValue(initialValues.display_frequency_weekday)
      : null,
  };

  const handleSubmit = (data: FormState<AdminPopupFormValues>) => {
    const { values: formValues } = data;
    const queryData = {
      ...formValues,
      button_text: formValues.button_text || null,
      from: formValues.from?.startOf('day').toISOString() || null,
      to: formValues.to?.endOf('day').toISOString() || null,
      display_frequency_weekday: formValues.display_frequency_weekday
        ? mapLabelValueToValues<number>(formValues.display_frequency_weekday)
        : null,
      company_ids: mapLabelValueToValues<number>(formValues.company_ids || []),
      show_on_modul_page: mapLabelValueToValues<string>(formValues.show_on_modul_page ?? []),
    };

    updateMutation.mutate(queryData, {
      onSuccess: () => {
        SuccessAlert('Popup úspěšně upraven');
        onClose();
      },
      onError: () => {
        ErrorAlert('Něco se pokazilo... :(');
      },
    });
  };

  return (
    <AdminPopupForm
      title="Upravit popup"
      mode="edit"
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      onCancel={onClose}
      companies={companies}
      isLoading={isLoading}
      mutationLoading={updateMutation.isLoading}
    />
  );
}
